import { createSafeJotaiLocalStorage } from '@/lib/jotai-storage';
import { type TemplateAuthorGroup, type TemplateCategory } from '@heidi/schema/ml-scribe';
import { type TemplateResponse } from '@heidi/templates/contracts';
import Fuse from 'fuse.js';
import { atom } from 'jotai';
import { atomFamily, atomWithStorage } from 'jotai/utils';
import type { SyncStorage } from 'jotai/vanilla/utils/atomWithStorage';

import { NoteStructureType, SortFilter } from './types';

////
// Base Atoms
////
export const presetTemplatesAtom = atom<TemplateResponse[]>([]);
export const pinnedTemplatesAtom = atom<TemplateResponse[]>([]);
export const searchInputAtom = atom('');

// The localStorage key for the persisted sort order, not a TanStack cache
// identity, so it deliberately carries no cache noun (`storage`, `cache`, …) —
// that keeps it out of the no-new-cache-key-declaration gate while staying
// importable (logoutStorage registers it; tests read it). Declared here, beside
// its `atomWithStorage`, so the persistence-manifest generator resolves it to
// the literal `key` (an imported constant falls back to an unresolved `keyExpr`
// that no longer tracks the actual persisted string).
export const TEMPLATE_PICKER_SORT_FILTER_KEY = 'heidi.templatePickerSortFilter';

const isSortFilter = (value: unknown): value is SortFilter =>
  typeof value === 'string' && (Object.values(SortFilter) as string[]).includes(value);

/**
 * SSR-safe, validating storage for the persisted sort order. Composes the
 * shared safe local-storage helper — which swallows the SecurityError/
 * QuotaExceededError that `window.localStorage` throws in restricted webviews —
 * and layers an enum check on top: a stale or malformed value (e.g. a renamed
 * enum member, or non-JSON bytes) falls back to `initialValue` so the picker
 * returns to its default grouped view instead of an unexpected flat order.
 */
const baseStorage = createSafeJotaiLocalStorage<SortFilter | null>();
const sortFilterStorage: SyncStorage<SortFilter | null> = {
  ...baseStorage,
  getItem: (key, initialValue) => {
    const value = baseStorage.getItem(key, initialValue);
    // Reject silently (return initialValue) and leave the stored value in
    // place, matching the sibling expandedGroupsAtom. A poisoned entry (e.g. a
    // renamed SortFilter member) self-heals on the next sort pick, which
    // overwrites it; no telemetry here by the same convention.
    return isSortFilter(value) ? value : initialValue;
  },
};

// getOnInit: true reads storage synchronously at module load so the first
// synchronous read of templateViewDerivedAtom already sees the persisted value,
// avoiding a default (grouped) → stored (flat) flicker on first paint — matching
// the sibling expandedGroupsAtom.
export const sortFilterAtom = atomWithStorage<SortFilter | null>(
  TEMPLATE_PICKER_SORT_FILTER_KEY,
  null,
  sortFilterStorage,
  { getOnInit: true }
);

// Session-scoped (not persisted): true once the user actively changes the sort
// via the picker control this session. `filters_applied.sort` analytics reads
// this to distinguish a session-applied sort from a value merely rehydrated
// from a previous session, keeping the metric's meaning stable across the
// persistence rollout.
export const sortFilterTouchedAtom = atom(false);
export const categoryFilterAtom = atom<TemplateCategory | null>(null);
export const authorFilterAtom = atom<TemplateAuthorGroup | null>(null);
/** NoteStructureTypeAtom refers to the type of "document" the user is creating */
export const noteStructureTypeAtom = atom<NoteStructureType>(NoteStructureType.Note);
export const hideProAtom = atom(false);
/** isJsonTemplateAtom refers to whether to use JSON content type */
export const isJsonTemplateAtom = atom(false);
/** isJsonTemplateAuditAtom refers to whether we need to post a JSON template for audit */
export const isJsonTemplateAuditAtom = atom(false);
/** Per-session Epic Hyperspace surface (legacy Epic FHIR auth, not Haiku, not
 * a sectional-native org, EHR queries settled), flag-independent. Written by
 * `usePublishHyperspaceSurface` from the session page, which stays mounted for
 * the whole session, so the value survives note-tab switches: regeneration
 * fired from other tabs (brain/voice hotkeys, tab right-click, shell template
 * picker) still converts. Entries are never removed (booleans, bounded by
 * session count; removing on unmount would reintroduce the reset). Read by
 * the stream-response regen arming, which owns the flag read. */
export const isHyperspaceSurfaceFamily = atomFamily(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (_sessionId: string) => atom(false)
);

/** Per-session count of failed or empty Hyperspace-armed sectional
 * conversions still owed a retry: the stored note is non-JSON after such a
 * failure, so `isJsonNoteInUse` alone would never re-arm. The
 * stream-response arm ORs a non-zero count in; a successful conversion
 * RESETs it, and the count is capped by the stream handler so a persistently
 * failing conversion cannot toast on every generation. Persisted per
 * session, because a reload or an Epic relaunch would otherwise drop it and
 * strand the note as markdown with no conversion and no toast. `getOnInit`
 * is load-bearing: only the non-React stream handler touches this family
 * (`jotaiStore.get/set`, never a component subscription), and without it
 * `atomWithStorage` reads storage only in `onMount`, which never fires. */
export const hyperspaceSectionalRestoreAttemptsFamily = atomFamily(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // Literal prefix kept inline so the persistence manifest records it verbatim.
  (_sessionId: string) =>
    atomWithStorage<number>(
      `heidi-hyperspace-sectional-restore-${_sessionId}`,
      0,
      createSafeJotaiLocalStorage<number>(),
      { getOnInit: true }
    )
);

////
// Derived Atoms
////
export const allTemplatesAtom = atom((get) => {
  const presetTemplates = get(presetTemplatesAtom);
  const pinnedTemplates = get(pinnedTemplatesAtom);

  // Preserve presetTemplates order so pinning a template does not shift its
  // position in downstream sorts (groupTemplatesByAuthor uses a stable sort,
  // so input order determines tie-break). When an id exists in both lists,
  // substitute the pinned version so its up-to-date fields (is_pinned, etc.)
  // take precedence. Pinned-only templates (not in presetTemplates) are
  // appended at the end as a defensive fallback.
  const pinnedById = new Map(pinnedTemplates.map((t) => [t.id, t]));
  const seen = new Set<string>();
  const merged: TemplateResponse[] = [];

  for (const t of presetTemplates) {
    seen.add(t.id);
    merged.push(pinnedById.get(t.id) ?? t);
  }
  for (const t of pinnedTemplates) {
    if (!seen.has(t.id)) {
      merged.push(t);
    }
  }
  return merged;
});

export const fuseInstanceAtom = atom((get) => {
  const templates = get(allTemplatesAtom);
  // ignoreLocation removes the positional penalty so a keyword that appears
  // mid-string (e.g. "Plantilla de … mano") ranks just as well as one at
  // position 0. Threshold stays at 0.3 to preserve false-positive protection
  // from PR #8076.
  return new Fuse(templates, {
    keys: ['name'],
    threshold: 0.3,
    ignoreLocation: true,
  });
});
